import React, { useState } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Shield,
  Lock,
  CheckCircle2,
} from "lucide-react";

export default function AdminRoles() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const translateRoleDescription = (description) => {
    if (!description) return '';
    const translationKey = `roleDesc.${description}`;
    const translated = t(translationKey);
    // If translation exists, return it; otherwise return the original description
    return translated !== translationKey ? translated : description;
  };

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["available-roles"],
    queryFn: () => adminService.getAvailableRoles(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => {
      // Note: Backend doesn't have delete role mutation yet
      // For now, we'll just show an alert
      throw new Error("Role deletion not yet implemented. System roles cannot be deleted.");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      setDeleteDialog(null);
    },
  });

  const filteredRoles = roles.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  const systemRoles = filteredRoles.filter(r => r.isSystem);
  const customRoles = filteredRoles.filter(r => !r.isSystem);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("staffRoles")}</h1>
          <p className="text-slate-500">
            {roles.length} {t("rolesCount")} ({customRoles.length} {t("customCount")}, {systemRoles.length} {t("systemCount")})
          </p>
        </div>
        <Link to={createPageUrl("AdminRoleForm")}>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            {t("createRole")}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchRolesPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Custom Roles */}
      {customRoles.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">{t("customRoles")}</h2>
            <p className="text-sm text-slate-500">{t("rolesYouveCreated")}</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("permissions")}</TableHead>
                <TableHead>{t("created")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customRoles.map((role) => (
                <TableRow key={role.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{role.name}</p>
                        {role.description && (
                          <p className="text-sm text-slate-500">{translateRoleDescription(role.description)}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {role.permissions && role.permissions.length > 0 ? (
                        <>
                          {role.permissions.slice(0, 3).map((perm, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {perm}
                            </Badge>
                          ))}
                          {role.permissions.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{role.permissions.length - 3} {t("morePermissions")}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-slate-400">{t("noPermissions")}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {format(new Date(role.createdAt), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <Link to={createPageUrl("AdminRoleForm") + `?id=${role.id}`}>
                          <DropdownMenuItem>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t("edit")}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteDialog(role)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t("delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* System Roles */}
      {systemRoles.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">{t("systemRoles")}</h2>
            <p className="text-sm text-slate-500">{t("predefinedRolesReadOnly")}</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("permissions")}</TableHead>
                <TableHead>{t("created")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {systemRoles.map((role) => (
                <TableRow key={role.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Lock className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{role.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {t("systemBadge")}
                          </Badge>
                        </div>
                        {role.description && (
                          <p className="text-sm text-slate-500">{translateRoleDescription(role.description)}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {role.permissions && role.permissions.length > 0 ? (
                        <>
                          {role.permissions.slice(0, 3).map((perm, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {perm}
                            </Badge>
                          ))}
                          {role.permissions.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{role.permissions.length - 3} {t("morePermissions")}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-slate-400">{t("noPermissions")}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {format(new Date(role.createdAt), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" disabled>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {filteredRoles.length === 0 && !isLoading && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <Shield className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{t("noRolesFound")}</h3>
          <p className="text-slate-500 mb-4">
            {search ? t("tryDifferentSearchTerm") : t("getStartedCreatingFirstRole")}
          </p>
          {!search && (
            <Link to={createPageUrl("AdminRoleForm")}>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                {t("createRole")}
              </Button>
            </Link>
          )}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteRole")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteRoleConfirmation").replace("{name}", deleteDialog?.name || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteDialog?.isSystem) {
                  toast.error(t("systemRolesCannotBeDeleted"));
                  setDeleteDialog(null);
                } else {
                  deleteMutation.mutate(deleteDialog.id);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}




